import { IconActivity, IconGlobe, IconHelpCircle, IconInfo, IconMessageSquare } from 'featherico';
import moment from 'moment';
import type { CurrentUserDto } from '../../../../../../types/UserDtos';
import { Dropdown, OptionLink, OptionWithModal, createLinkOption } from '../../../../components/Dropdown';
import ActivityDot from '../../../../components/Notifications/Dot/ActivityDot';
import { useAppContext } from '../../../../hooks/useAppContext';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import { useNow } from '../../../../hooks/useNow';
import { isAnyAdmin } from '../../../../services/User';
import { JustLaunchedMenuItems } from '../../../JustLaunched/MenuItems';
import { getActiveReleaseNotes } from '../../../JustLaunched/service';
import IntercomSupportLink from '../../../Navigation/SupportLink/IntercomSupportLink';
import { HrListSupport } from '../../../Navigation/SupportLink/WithHrListSupportModal';
import styles from './style.m.less';

const RELEASE_NOTES_LAST_VIEWED_STORAGE_KEY = 'release-notes-last-viewed';

const OptionHelpLink = createLinkOption((props: { onClick?: () => void }) => <IntercomSupportLink {...props} />);

export default function HelpMenu() {
    const { user: currentUser } = useAppContext();
    const now = useNow(moment.duration(1, 'hour'));
    const { lastViewed, updateLastViewed, hasNewReleaseNotes } = useReleaseNotes(currentUser, now);

    return (
        <Dropdown
            kind="PLAIN"
            showChevron={false}
            ariaLabel={`Help menu`}
            label={
                <div className={styles.iconContainer}>
                    <IconHelpCircle />
                    {hasNewReleaseNotes && (
                        <div className={styles.dotContainer}>
                            <ActivityDot />
                        </div>
                    )}
                </div>
            }
            eventTracking={{ category: 'navigation header help', action: 'toggle' }}
            className="top-aligned left-aligned"
        >
            <OptionLink
                href="https://intercomdocs.small-improvements.com/en/"
                openInNewTab={true}
                eventTracking={{ category: 'navigation header help', action: 'click', label: 'help center' }}
            >
                <IconInfo /> Help Center
            </OptionLink>
            {isAnyAdmin(currentUser) ? (
                <OptionHelpLink />
            ) : (
                <OptionWithModal
                    modalProps={{
                        size: 'LG_WIDTH',
                        title: 'Get Support',
                        body: () => <HrListSupport />,
                    }}
                    eventTracking={{
                        category: 'navigation header help',
                        action: 'click',
                        label: 'get help non-admin',
                    }}
                >
                    <IconMessageSquare /> Get Support
                </OptionWithModal>
            )}
            <OptionLink
                href="https://status.small-improvements.com"
                openInNewTab={true}
                eventTracking={{ category: 'navigation header help', action: 'click', label: 'system status' }}
            >
                <IconActivity /> System Status
            </OptionLink>
            <OptionLink
                href="https://small-improvements.com"
                openInNewTab={true}
                eventTracking={{ category: 'navigation header help', action: 'click', label: 'si home page' }}
            >
                <IconGlobe /> Website
            </OptionLink>
            <JustLaunchedMenuItems now={now} lastViewed={lastViewed} onHide={updateLastViewed} />
        </Dropdown>
    );
}

function useReleaseNotes(currentUser: CurrentUserDto, now: Date) {
    // Use “last login” date as initializer, to avoid bothering people with old release notes.
    // See the annotation comment on `previousLoginDate` in `User.java` for why we are not
    // using `lastLoginDate` here.
    const [lastViewedVal, setLastViewedVal] = useLocalStorage(
        RELEASE_NOTES_LAST_VIEWED_STORAGE_KEY,
        new Date(currentUser.previousLoginDate ?? 0).toISOString()
    );
    const lastViewed = new Date(lastViewedVal);
    return {
        lastViewed,
        updateLastViewed: () => setLastViewedVal(new Date().toISOString()),
        hasNewReleaseNotes:
            getActiveReleaseNotes(now.getTime()).find(r => new Date(r.date).getTime() > lastViewed.getTime()) !==
            undefined,
    };
}
